/**
 * GENERATED FILE — DO NOT HAND-EDIT.
 * Produced by context/scripts/publish-glossary.mjs from docs/glossary.json.
 * Run: node context/scripts/publish-glossary.mjs
 */

export const GLOSSARY_ENTRIES = [
  {
    "id": "activation",
    "term": "Activation",
    "aliases": [
      "activations",
      "activation event"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "One certificate becoming active. This is the unit Vera bills for, counted in the billing period it happens in.",
    "long": "A certificate is charged once, when it activates — not when it was created and not when its tag was made. What causes that is the [activation trigger](term:activation-trigger) that applies to it — the company default, or an override on its product or on the certificate itself, so a brand on On sale is charged as items sell while a brand on On creation is charged as certificates are minted; a brand can also activate a certificate directly from the platform, which counts the same. Activations covered by your [included](term:included-activations) or [granted](term:granted-activations) balance are not billed; the rest are priced on the [graduated ladder](term:graduated-activation-rate). One activation is one certificate everywhere except an [indivisible certificate group](term:indivisible-group), which goes live together and is [charged as a single activation](term:group-activation) however many certificates that is. [Sample data](term:intro) and demo certificates are never billed, even when they sit in a company that is.",
    "seeAlso": [
      "activation-trigger",
      "certificate-status-active",
      "included-activations",
      "activation-total-cost",
      "group-activation"
    ],
    "docs": [
      "company-settings/settings-billing-overview",
      "company-settings/settings-features"
    ]
  },
  {
    "id": "activation-price-tier",
    "term": "Activation price tier",
    "aliases": [
      "price tier",
      "next price tier",
      "price break",
      "graduated activation pricing",
      "activation rate tier"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A volume threshold on the activation pricing ladder. Cross it and every further activation this period costs the next rate down, from $25 to $5.",
    "long": "Pricing is [graduated](term:graduated-activation-rate), meaning each band prices only the [activations](term:activation) that fall inside it. Crossing a threshold makes the next activation cheaper and never re-prices the ones you have already used, which is deliberate: under whole-volume pricing a brand just past a threshold would owe less in total than one just below it, and a bill that falls as usage rises is both unfair and gameable. The practical effect is that your average cost per activation falls as you grow, while any given month's bill still rises with use. It is the rate ladder, not your subscription [plan](term:plan) — the same schedule applies to every plan.",
    "seeAlso": [
      "activation",
      "included-activations",
      "granted-activations",
      "activation-total-cost"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "activation-total-cost",
    "term": "Total activation cost",
    "aliases": [
      "activation charge",
      "activation cost",
      "total activation charge"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "all activations this period priced through the rate ladder, less your included allowance credited at the entry rate",
    "unit": "USD",
    "short": "What this period's activations cost. Every activation is priced on the volume ladder, then your plan's included allowance is credited back at the $25 entry rate.",
    "long": "Read it as two steps. First the period's whole [activation](term:activation) count is priced through the [graduated ladder](term:graduated-activation-rate), which gives the gross value; then your [plan's allowance](term:included-activations) is credited back at the ladder's entry rate, capped at what you actually used — consuming 2 of 12 credits saves the value of 2, not of 12. Pricing the ladder on the billable count instead, as though the included ones had never happened, is a different and larger figure. The number covers activations only; your plan's base fee is billed separately.",
    "seeAlso": [
      "activation",
      "activation-price-tier",
      "included-activations",
      "granted-activations"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "activation-trigger",
    "term": "Activation trigger",
    "aliases": [
      "activation triggers",
      "certificate activation"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The event that makes a certificate Active, and therefore billable. Set for the whole company, or overridden for one product or one certificate.",
    "long": "This is a billing decision as much as a workflow one, because a certificate is charged as an [activation](term:activation) at the moment it activates. On creation activates every certificate as soon as it exists — including a whole [box of tags](term:package) the instant you assign it to a product, which commits the spend for the box in one click. On sale, the default, keeps certificates [inactive](term:certificate-status-inactive) until the customer claims ownership, so cost follows revenue. The company default is set under Settings, and a single product or a single certificate can override it — useful when some items go to retail and others ship direct. The most specific setting wins: certificate, then product, then the company default. On location arrival activates when one of your own locations confirms an item arrived — a shop, a production site, anywhere in your address book. Send an item to a location from its certificate, and confirm arrival there; confirming is what activates it, so nothing is charged while stock is in transit.",
    "seeAlso": [
      "activation",
      "certificate",
      "certificate-status-active",
      "package"
    ],
    "docs": [
      "company-settings/settings-features",
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "ai-query",
    "term": "AI query",
    "aliases": [
      "ask query"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "One question put to Ask, the panel that answers in plain language from your own product and scan data. Each plan lists a monthly number.",
    "long": "Ask answers from your data rather than from the internet, so what it can tell you is bounded by what Vera holds: your products, your certificates, your scans. A question that would need something Vera does not record is one it will decline rather than guess at.",
    "seeAlso": [
      "plan",
      "scan"
    ]
  },
  {
    "id": "authenticator-app",
    "term": "Authenticator app",
    "aliases": [
      "totp",
      "authenticator",
      "time-based one-time password"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "An app on your phone that generates a new six-digit sign-in code every 30 seconds. It works offline, and the codes are never sent over text.",
    "long": "Setting one up means scanning a QR code once. From then on the app and Vera independently compute the same six-digit code from a shared secret and the current time, so nothing has to be sent anywhere when you sign in.\n\nThat is why it is the stronger of the two options: a text message can be intercepted or redirected to a swapped SIM, while an authenticator app has nothing in transit to intercept. Any standard app works — Google Authenticator, 1Password, Authy and others.",
    "seeAlso": [
      "two-factor-authentication",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "authenticity-verification-rate",
    "term": "Authenticity verification rate",
    "aliases": [
      "authenticity rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of scans that proved the item genuine, rather than only showing that a link was opened. Not computed yet; the card shows a dash.",
    "long": "Not every tag can prove itself on each tap, and opening a certificate's web link is recorded as a scan without proving anything — so this is as much a measure of what your tags are capable of as of what people encountered. [Verified](term:scan-status-verified) is what a proof establishes; [opened](term:scan-status-opened) is the outcome that establishes nothing beyond a visit.\n\n⚠️ The card is a placeholder today and renders a dash. The definition is here so that the figure means the same thing on the day it arrives as it does in this sentence.",
    "formula": "Verified scans ÷ all scans in range.",
    "seeAlso": [
      "scan-status-verified",
      "scan-status-opened",
      "verify",
      "invalid-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "average-resale-price",
    "term": "Average resale price",
    "aliases": [
      "avg resale price"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average declared price of a completed resale in the period. Resales with no declared price are left out, not counted as zero.",
    "long": "The price is what the seller declared at the time — not a figure Vera verified, and not a payment it processed — so it is only as good as sellers' willingness to state one.\n\nExcluding unpriced resales rather than zeroing them keeps the average honest, but it does mean the figure describes a subset of [Resales](term:resales) and the two counts do not match. Resales declared in a currency other than your reporting currency are left out as well: converting them at today's rate would silently relabel one currency as another.",
    "formula": "Mean declared price over completed resales in range that carry a price.",
    "seeAlso": [
      "resales",
      "sell",
      "resales-per-resold-item"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "brand",
    "term": "Brand",
    "aliases": [
      "brands",
      "company",
      "maker"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The company that made the item and issued its certificate. Every provenance ends at the brand, because that is where the item started.",
    "long": "The brand is the origin of the record, not merely a name attached to it: the [certificate](term:certificate) exists because the brand created it and paired it to a [tag](term:tag) before the item was sold. That is what makes the [provenance](term:provenance) a chain rather than a claim — read it backwards and every completed handover leads to the company that made the thing. A brand can see that its certificates exist and how often they are scanned; it does not gain any right to the item, and ownership only ever moves by [transfer](term:transfer).",
    "seeAlso": [
      "certificate",
      "product",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "certificate",
    "term": "Certificate",
    "aliases": [
      "certificates",
      "digital certificate"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The digital record of authenticity for one physical item, owned by a person and paired with a tag.",
    "long": "A certificate stands for one individual item, not for the [product line](term:product) it belongs to: a hundred units of the same handbag are a hundred certificates against one product. It usually exists before the item is finished — certificates are minted for a whole box of [tags](term:tag) when that box is assigned to a product — and it stays [inactive](term:certificate-status-inactive) until the brand makes it live, which for most brands is the moment the item is sold. From then on it is what a scan of its tag resolves to, and what carries the item's owner, its [provenance](term:provenance) and its scan history.",
    "seeAlso": [
      "tag",
      "owner",
      "provenance",
      "activation-trigger"
    ],
    "docs": [
      "certificates/certificate-scans",
      "tags/assign-product-to-package"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "certificate-coverage",
    "term": "Certificate coverage",
    "aliases": [
      "catalogue coverage"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of the products in your catalogue carrying at least one Vera certificate. Not computed yet; the card shows a dash.",
    "long": "Counted over [products](term:product) — designs — and not over units made, so a product with one certificate against a run of ten thousand counts as covered exactly like a fully certified one. It is a measure of catalogue reach, not of how much stock is protected.\n\nIt is also the factor [Return fraud prevented](term:return-fraud-prevented) is scaled by, on the reasoning that there was nothing to verify on goods that were never certified.",
    "formula": "Products with at least one certificate ÷ all products.",
    "seeAlso": [
      "product",
      "certificate",
      "return-fraud-prevented"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "certificate-group",
    "term": "Certificate group",
    "aliases": [
      "certificate groups",
      "grouped certificates",
      "group of certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Several certificates linked under one parent certificate, so an object made of separately tagged parts is tracked and sold as one set.",
    "long": "Some objects are one thing to a customer and several tagged parts to you: a two-piece luggage set, a watch with a separately certified strap, a chair sold with its cushion. A group is how those are held together. Each part keeps its own certificate and its own tag, and a parent certificate is created above them to stand for the whole. Every group is either divisible or indivisible, and that choice is fixed when the group is created. You can build a group as you create the certificates, or group certificates you already hold, as long as none of them has ever been transferred to an owner.",
    "seeAlso": [
      "parent-certificate",
      "child-certificate",
      "divisible-group",
      "indivisible-group",
      "certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "certificate-status-active",
    "term": "Active",
    "aliases": [
      "active status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that is live for its owner. A certificate reaches this state once the item is sold, or when the brand activates it directly.",
    "long": "Active is what a certificate becomes once it is out in the world with an [owner](term:owner). Before that it sits [inactive](term:certificate-status-inactive) with the [brand](term:brand) that made it, and for most brands the change happens at the moment the item is sold and its first owner claims it. A brand can also make a certificate active directly, without waiting for a sale. It only ever happens once — a certificate that is already active is not activated again.",
    "seeAlso": [
      "certificate-status-inactive",
      "owner",
      "activation",
      "activation-trigger",
      "certificate-status-created"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "certificate-status-created",
    "term": "Created",
    "aliases": [
      "created status"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A certificate that exists but has not yet met its activation trigger, so it is not yet live for an owner.",
    "long": "Created is the resting state for stock made ahead of demand. The item may already be tagged and sitting in a box — certificates are minted with the tags when a [package](term:package) is assigned to a product — but nothing has met the [trigger](term:activation-trigger) yet, so no owner sees it and nothing has been billed. It becomes [Active](term:certificate-status-active) the moment its trigger fires, which for most companies is the customer claiming ownership.",
    "seeAlso": [
      "activation-trigger",
      "certificate-status-active",
      "package"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "certificate-status-inactive",
    "term": "Inactive",
    "aliases": [
      "inactive status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that exists but is not yet live for an owner, normally because the item has not been sold yet.",
    "long": "Inactive is the ordinary resting state of stock, not a fault. Certificates are created ahead of demand — in a batch, when a box of [tags](term:tag) is assigned to a [product](term:product) — so an item can be tagged, boxed and sitting in a warehouse with its certificate already waiting. Nothing about the item is wrong; it simply has no [owner](term:owner) yet. Scanning the tag of an inactive item shows the product it belongs to but no ownership, because there is none to show.",
    "seeAlso": [
      "certificate-status-active",
      "certificate-status-created",
      "certificate"
    ]
  },
  {
    "id": "certificate-status-lost",
    "term": "Lost",
    "aliases": [
      "lost status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported missing. It stays theirs, and anyone who scans the tag is shown that it has been reported lost.",
    "long": "Reporting an item lost does not give it up: ownership is unchanged and the certificate stays in your account, which is the point — you keep the claim to it while making the loss visible to whoever finds it. Because the report is attached to the [certificate](term:certificate) rather than to the [tag](term:tag), it is seen by anyone who taps the item, so a finder who scans it learns immediately that it is looked for. Marking the item found returns it to its previous state.",
    "seeAlso": [
      "certificate-status-stolen",
      "owner",
      "scan"
    ]
  },
  {
    "id": "certificate-status-stolen",
    "term": "Stolen",
    "aliases": [
      "stolen status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported stolen. It stays theirs, and anyone who scans the tag is shown that it has been reported stolen.",
    "long": "Stolen is kept separate from [Lost](term:certificate-status-lost) rather than folded into one 'missing' state, because the two mean different things to whoever is holding the item. Lost invites a finder to return it; stolen tells a prospective buyer that the person offering it has no title to it, which is the moment a resale is most likely to be stopped. Ownership does not move either way — an item cannot be [transferred](term:transfer) away from you by being taken — so the certificate remains yours until you transfer it yourself.",
    "seeAlso": [
      "certificate-status-lost",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-status-transfer",
    "term": "Transfer",
    "aliases": [
      "transfer status",
      "in transfer",
      "pending transfer"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate part-way through changing hands. It stays with the current owner until the person receiving it accepts.",
    "long": "This one status covers every stage of a handover in flight — sent and unopened, waiting on the recipient, or with a cancellation requested — because from the outside they amount to the same thing: the item is spoken for but has not moved. Nothing about ownership changes while a certificate sits here, which is what makes a [transfer](term:transfer) safe to send. It resolves in one of two directions: the recipient accepts and becomes the [owner](term:owner), or the transfer is cancelled and the certificate stays exactly where it was.",
    "seeAlso": [
      "transfer",
      "gift",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-view",
    "term": "Certificate view",
    "aliases": [
      "view",
      "views",
      "share link",
      "custom view",
      "public view"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A shareable link to one of your certificates, with its own setting for who may open it and how much of the item's detail it shows.",
    "long": "A view exists so that showing someone an item is not the same as giving them everything about it. Each one carries two independent controls: who can open it — anyone holding the link, or only people you have invited by email — and what it reveals, since ownership, its [history](term:provenance), sale prices and scans can each be hidden. You can keep several at once, which is the point: a link for a prospective buyer that shows provenance but not what you paid, and a private one that shows everything.",
    "seeAlso": [
      "certificate",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "child-certificate",
    "term": "Child certificate",
    "aliases": [
      "child certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A tagged certificate that belongs to a group. It is one of the physical parts the group's object is made of, and it is what a customer actually scans.",
    "long": "Every member of a group other than the parent is a child. A child keeps its own tag, its own product and its own page, and it carries its group's divisibility so any surface showing it knows which kind of group it is in. What a scan of its tag opens depends on that: a divisible child is independently sellable, so its tag opens its own certificate, while an indivisible child's tag opens the parent's certificate instead, because the set is what was sold.",
    "seeAlso": [
      "parent-certificate",
      "group-member",
      "certificate-group",
      "tag"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "counterfeit-deflections",
    "term": "Counterfeit deflections",
    "aliases": [
      "deflections"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "An estimate of how many counterfeit encounters your tags caught, inferred from verification failures. Not computed yet; the card shows a dash.",
    "long": "⚠️ An estimate rather than a measurement, and NOT the same thing as [Invalid scans](term:invalid-scans). Most rejections are not counterfeits — a damaged tag, a replayed read, an ordinary misread — so turning a rejection count into an encounter count needs a rate that only you can supply.\n\n⚠️ Note also that there is a similarly named CURRENCY metric, Counterfeit deflection value, which contributes to [Value delivered](term:value-delivered). This one is a count of incidents and does not. They are one letter apart and are deliberately never matched to each other by name.",
    "formula": "Verification failures judged to represent a genuine counterfeit encounter.",
    "seeAlso": [
      "invalid-scans",
      "unattributed-invalid-scans",
      "value-delivered"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "countries-reached",
    "term": "Countries reached",
    "aliases": [
      "geographic reach"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many distinct countries your scans came from in the period, counting only scans whose location resolved to a country.",
    "long": "A scan can carry coordinates and still have no country attached to them, so this can read lower than the number of pins on the map beside it. The card names how many located scans are in that state rather than dropping them quietly — \"0 countries\" next to a visible pin reads as a bug unless the page accounts for it. Scans with no location at all are not part of the measure in the first place; [Scan location accuracy](term:scan-location-accuracy) is how many of those there are.",
    "formula": "Count of distinct resolved countries across scans in range.",
    "seeAlso": [
      "scan",
      "scan-location-accuracy",
      "repeat-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "divisible-group",
    "term": "Divisible group",
    "aliases": [
      "divisible",
      "divisible certificate group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A group whose parts can be sold separately. Each member activates and is billed on its own, and the group can be broken up at any time.",
    "long": "Divisible describes how the object is sold, not how it is stored: the parts are related but each one can leave on its own. Nothing cascades, so a divisible group of three activated one at a time is three activations, at the ordinary rate for each. Because the parts are what sell, each keeps its own page and its own tag opens it. The parent is only a container, so it needs no product of its own, and a divisible group may hold as few as one child. It can be split or have members removed at any point, whatever their status.",
    "seeAlso": [
      "indivisible-group",
      "certificate-group",
      "split-group",
      "group-activation"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "encode",
    "term": "Encode",
    "aliases": [
      "encoding",
      "encoded"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Writing a tag's identity and verification material onto the chip, which is what makes it readable and checkable.",
    "long": "Encoding prepares the chip itself: it writes the [tag](term:tag)'s own identity and, on families that can prove identity per tap, the secret material a later scan verifies against. It happens once, in Vera's facility or on a desk [reader](term:reader), and it is what makes a tag verifiable at all. It does not decide which item the tag belongs to — that pairing is a separate record in Vera — which is why a tag can be encoded and stocked long before anyone knows what it will end up on.",
    "seeAlso": [
      "tag",
      "reader",
      "certificate",
      "package"
    ]
  },
  {
    "id": "field-mapping",
    "term": "Field mapping",
    "aliases": [
      "field map",
      "field mappings"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The correspondence you define between your store's product fields and Vera's, so synced products land in the right places.",
    "long": "Your store and Vera describe a product differently, so an import has to be told which of your fields fills which of Vera's — title, images, price, and any metafields you keep. Mapping is set per connected store and grouped by what it fills: Product, Owner or Tag. A field your store offers but Vera's importer cannot yet read is shown greyed out rather than hidden, because mapping to it would quietly do nothing. Mapping decides what a field is worth on arrival; [sync mode](term:sync-mode) decides whether later edits keep overwriting it.",
    "seeAlso": [
      "sync-mode"
    ],
    "docs": [
      "integrations/settings-field-mapping",
      "integrations/field-mapping-catalog"
    ]
  },
  {
    "id": "fulfillment-visibility",
    "term": "Fulfillment visibility",
    "aliases": [
      "scans per certificate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average number of scans recorded per certificate — how often a typical item is seen. Not computed yet; the card shows a dash.",
    "long": "A blunt engagement measure rather than a logistics one, despite the name: it divides all scans by all certificates, so certificates never scanned pull it down and a single heavily scanned item pulls it up.\n\n[Repeat scan rate](term:repeat-scan-rate) answers the neighbouring question more precisely, because it counts over items rather than over scans. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Total scans ÷ total certificates.",
    "seeAlso": [
      "repeat-scan-rate",
      "scan",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "gift",
    "term": "Gift",
    "aliases": [
      "gifting",
      "give"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to someone without a sale. The certificate moves to them once they accept, and the handover joins the item's provenance.",
    "long": "A gift is a [transfer](term:transfer) with no price attached, and it behaves like every other transfer: the item stays with you until the person receiving it accepts, so an unclaimed gift can be cancelled and a mistyped address never costs anyone the item. Once accepted it becomes a permanent entry on the [provenance](term:provenance), recorded as a gift rather than a sale, which is what lets a later reader tell the difference between an item that was sold on and one that was passed to family.",
    "seeAlso": [
      "transfer",
      "sell",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "graduated-activation-rate",
    "term": "Graduated activation rate",
    "aliases": [
      "graduated pricing",
      "activation ladder",
      "rate ladder"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "each band of the period's activations priced at that band's own rate, then summed",
    "unit": "USD",
    "short": "Activations are priced in bands, from $25 each down to $5 as your total for the period grows. Each band bills at its own rate, like tax brackets.",
    "long": "Graduated is the load-bearing word, and it is the opposite of a bulk discount that reprices everything. Crossing into a cheaper band never makes your earlier [activations](term:activation) cheaper: they stay billed at the rate of the band they fell in. Six hundred activations are therefore the first hundred at $25, the next four hundred at $20 and the last hundred at $16 — a blend, not six hundred at the cheapest rate reached. The bands are the same on every [plan](term:plan); plans differ in how many activations are [included](term:included-activations) before the ladder applies.",
    "seeAlso": [
      "activation",
      "activation-price-tier",
      "activation-total-cost",
      "included-activations"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "granted-activations",
    "term": "Granted activations",
    "aliases": [
      "earned activations",
      "granted / earned activations",
      "activation credits"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Extra activation credits added to your account by Vera, on top of your plan's allowance. They do not expire and share one balance with your included activations.",
    "long": "Vera adds these by hand — service recovery, a trial before a subscription starts, an expansion incentive — and they are independent of any [plan](term:plan), so a company with no [subscription](term:subscription) at all can hold them. They go into the same balance as the allowance your plan accrues each period, and an [activation](term:activation) simply draws that balance down: there is no separate pool spent first or last. The figure shown is the lifetime total granted, not the amount still available.",
    "seeAlso": [
      "activation",
      "included-activations",
      "activation-price-tier",
      "activation-total-cost"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "group-activation",
    "term": "Group activation",
    "aliases": [
      "activating a group",
      "group activation charge"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What one Activate covers when the certificate is in a group. An indivisible group goes live together for one charge. A divisible one bills per member.",
    "long": "On an indivisible group, activating spreads to every member that is not already live, in both directions: from the parent down to its children, and from any child up to the parent and across to the rest. However many certificates that takes live, it is charged as a single activation, so the price does not depend on which row you clicked. A divisible group does not spread at all, and each member is charged as its own activation. The confirmation dialog states both numbers before you commit, and it also warns you when this is the activation that makes an indivisible group permanent.",
    "seeAlso": [
      "activation",
      "indivisible-group",
      "divisible-group",
      "group-member"
    ],
    "docs": [
      "certificates/certificates",
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "group-member",
    "term": "Group member",
    "aliases": [
      "group members",
      "member of a group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Any certificate in a group, the parent included. Counts of how many members will activate, or must be unowned, always count the parent as one of them.",
    "long": "Member and child are not the same word. A child is a tagged part; a member is any certificate in the group, so the members are the parent plus all of its children. The difference is worth knowing because it is the members, not the children, that every group-wide rule is counted over. Activating an indivisible group takes every member live, and the confirmation count includes the parent. Whether an indivisible group may still be broken up is decided by checking that every member, parent included, is still unactivated and unowned.",
    "seeAlso": [
      "certificate-group",
      "parent-certificate",
      "child-certificate",
      "group-activation"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "in-transit-rate",
    "term": "In-transit rate",
    "aliases": [
      "in transit rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of your certificates sitting in an unfinished ownership transfer right now. A snapshot, not a figure for the period. Not computed yet.",
    "long": "A live proportion rather than a period measure: a certificate is either mid-transfer now or it is not, and \"in transit during March\" is not a state it has.\n\nClose to [In progress transfers](term:transfers-in-progress) but not the same figure — that one counts transfers started inside the date range, this one counts certificates regardless of when their transfer began. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Certificates currently in a transfer state ÷ all certificates.",
    "seeAlso": [
      "certificate-status-transfer",
      "transfers-in-progress",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "included-activations",
    "term": "Included activations",
    "aliases": [
      "plan included activations",
      "included activations / mo",
      "activation allowance"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The certificate activations your plan covers each billing period at no extra charge. Anything past them is billed at the graduated rate. Their value usually exceeds the plan fee.",
    "long": "Each [plan](term:plan)'s allowance is calibrated to be worth at least its base fee, so the subscription pays for itself before you spend anything on top. The allowance accrues once per billing period, and unused ones [roll over](term:rollover) and are kept for as long as you stay subscribed, so a quiet month is not forfeited. On the bill they are credited at the [ladder](term:graduated-activation-rate)'s entry rate rather than at the cheaper marginal rate a high-volume month reaches, which means a credit is worth the same in every month whatever your volume.",
    "seeAlso": [
      "activation",
      "granted-activations",
      "activation-price-tier",
      "activation-total-cost",
      "activation-trigger"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "indivisible-group",
    "term": "Indivisible group",
    "aliases": [
      "indivisible",
      "indivisible certificate group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A group sold only as one unit. Activating any member activates them all for a single activation charge, and once live the group can never be broken up.",
    "long": "Indivisible is a claim about the physical object: the parts are not separately sellable, so the group is treated as one certificate everywhere it counts. Activating any member activates the whole group, and the whole group is charged as one activation, whichever member you started from. A scan of any part opens the parent's certificate, because the set is what the customer bought. It needs at least two members and the parent must have a product, since the parent is the unit that billing and inventory follow. It can only be broken up while every member is still unactivated and unowned. After that it is permanent.",
    "seeAlso": [
      "divisible-group",
      "certificate-group",
      "group-activation",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "installation",
    "term": "Installation",
    "aliases": [
      "installations",
      "install",
      "embed",
      "snippet"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The code you embed in your own site so certificates open on your pages, under your domain, instead of on a Vera address.",
    "long": "An installation changes where a [scan](term:scan) lands, not what it resolves to: the [certificate](term:certificate), its provenance and its scan history are the same either way. What it buys is continuity — a customer who taps an item stays on your site rather than being handed to a domain they do not recognise, at the exact moment they are checking whether something is genuine. Until an installation is in place, scans resolve to your Vera [subdomain](term:subdomain), which works identically and simply is not yours.",
    "seeAlso": [
      "subdomain",
      "scan",
      "certificate"
    ],
    "docs": [
      "integrations/settings-installation"
    ]
  },
  {
    "id": "integration",
    "term": "Integration",
    "aliases": [
      "integrations",
      "connection"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A connection between Vera and a system you already run, such as your online store, so products and orders arrive without being entered twice.",
    "long": "An integration is a standing link rather than a one-off import: once connected, changes on the other side keep arriving. Two settings decide what it actually does. The [field mapping](term:field-mapping) says which of their fields becomes which of yours, and the [sync mode](term:sync-mode) says whether a later change on their side is allowed to overwrite what is in Vera. Connecting a system is therefore the smallest part of the job — the mapping is what determines whether the data lands where you expect.",
    "seeAlso": [
      "field-mapping",
      "sync-mode",
      "product"
    ],
    "docs": [
      "integrations/settings-field-mapping"
    ]
  },
  {
    "id": "intro",
    "term": "Sample data",
    "aliases": [
      "intro data"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Records Vera added so a new company has something to explore. They are not your data, they are never billed, and you can delete them.",
    "long": "Sample data sits beside real records rather than in a separate space, because a company starts using the product before it has any records of its own and a walled-off sandbox would teach the wrong thing. Everything seeded this way is badged, so nothing is ambiguous about which rows are yours.",
    "seeAlso": [
      "product",
      "certificate"
    ]
  },
  {
    "id": "invalid-scan-rate",
    "term": "Invalid scan rate",
    "aliases": [
      "invalid rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of your scans that were rejected as invalid. Both halves are counted over the same population, so it describes that population.",
    "long": "⚠️ The denominator is the scan record itself — the very rows the numerator is drawn from, under the same filters — and NOT the wider certificate action log, which also records link openings that were never a tag read. Both can reasonably be called \"total scans\", the two denominators differ substantially, and picking the wrong one moves this number a long way while looking entirely correct.\n\nBecause both halves come from one population under one set of filters, this is a property of that population rather than a ratio between two differently scoped counts. The card prints its base for the same reason [Repeat scan rate](term:repeat-scan-rate) does: 100% over a single scan is correct and meaningless.\n\n⚠️ Filtering the page to invalid scans makes every figure here exactly 100% — correctly, since the filter makes the two halves the same rows, and uselessly.",
    "formula": "Invalid scans ÷ all scans in range, both counted from the scan record under the same filters.",
    "seeAlso": [
      "invalid-scans",
      "scan-status-invalid",
      "unattributed-invalid-scans",
      "repeat-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "invalid-scans",
    "term": "Invalid scans",
    "aliases": [
      "rejected scans"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Scans Vera rejected in the period — a failed cryptographic proof, a replayed counter, or a tag it does not recognise.",
    "long": "Counted only over scans that reached one of your [certificates](term:certificate). A scan of a tag Vera never issued matches no certificate at all, and every filter on this figure reaches through that link, so those cannot appear here — they are reported alongside as [unattributed invalid scans](term:unattributed-invalid-scans), and they are the more interesting of the two.\n\nWhat a rejection means depends on the tag and on how the link was reached; [invalid](term:scan-status-invalid) is the outcome itself, and the neighbouring scan statuses separate a tampered read from a merely stale link.",
    "formula": "Count of scans in range flagged invalid, among scans matched to one of your certificates.",
    "seeAlso": [
      "scan-status-invalid",
      "invalid-scan-rate",
      "unattributed-invalid-scans",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "inventory-available",
    "term": "Available",
    "aliases": [
      "available tags",
      "on hand"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags in your hands right now, received and ready to apply. Of the three counts, this is the only one you can actually use today.",
    "long": "A tag counts here once you have scanned its [package](term:package) in, which is why Available can lag a delivery that has physically arrived — the count follows the receipt scan, not the courier. Everything downstream is built on this number and not on the other two: [Capacity](term:inventory-capacity) is Available minus demand, and the stock warning fires on Capacity. Note that this column once meant spare capacity rather than possession, so older screenshots and notes may use the word differently.",
    "seeAlso": [
      "inventory-ordered",
      "inventory-incoming",
      "inventory-capacity"
    ]
  },
  {
    "id": "inventory-capacity",
    "term": "Capacity",
    "aliases": [
      "spare capacity",
      "tag capacity"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "Available − Uncertified products",
    "unit": "product units",
    "short": "How many more units you could certify with the tags already in hand. It is signed: a negative number is a shortfall of that many tags, not a maximum of zero.",
    "long": "The sign is the whole value of the figure. A row reading −26 says you are twenty-six tags short of covering the stock you hold, which is the number you would order on — flooring it at zero would collapse every short line into one indistinguishable bucket. [Ordered](term:inventory-ordered) and [Incoming](term:inventory-incoming) are excluded on purpose, because neither can be applied to a product today. One consequence to expect: the Capacity card above the table is not the column added up. The card floors each tag type at zero first, because spare tags of one type cannot cover a shortfall in another.",
    "seeAlso": [
      "inventory-available",
      "inventory-uncertified-products",
      "inventory-threshold"
    ]
  },
  {
    "id": "inventory-incoming",
    "term": "Incoming",
    "aliases": [
      "in transit",
      "incoming tags"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags Vera has shipped that have not reached you yet. They become Available when you scan the package in on arrival.",
    "long": "Incoming is deliberately excluded from [Capacity](term:inventory-capacity) and from the stock warning, and the reason is the same in both cases: a tag in a van cannot be applied to a product today, so counting it as readiness would overstate what you can actually do. It is shown because it changes what you should do about a shortfall — a gap that is already in transit needs patience rather than another order. Scanning the [package](term:package) in on receipt is what moves these into [Available](term:inventory-available); until that happens they stay here regardless of where the parcel physically is.",
    "seeAlso": [
      "inventory-ordered",
      "inventory-available",
      "package"
    ]
  },
  {
    "id": "inventory-ordered",
    "term": "Ordered",
    "aliases": [
      "at vera",
      "ordered tags"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags you have paid for that Vera is still preparing. Nothing has shipped yet, so none of these can be applied to a product today.",
    "long": "Ordered, [Incoming](term:inventory-incoming) and [Available](term:inventory-available) are three stages of the same journey, and a tag is in exactly one of them, so the three never double-count. This is a live count rather than a running total of what you have ever bought: it falls as tags ship, and reaching zero means everything you ordered has left Vera, not that you never ordered anything. Read together with Available it answers the question that matters when stock is low — zero available against a hundred ordered means stock is coming; zero against zero means you need to buy tags.",
    "seeAlso": [
      "inventory-incoming",
      "inventory-available",
      "package"
    ]
  },
  {
    "id": "inventory-threshold",
    "term": "Threshold",
    "aliases": [
      "reorder threshold",
      "low stock threshold",
      "alert threshold"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "The level your spare capacity has to fall to before Vera warns you. Zero means no level has been set, not “warn me at zero”.",
    "long": "The threshold is compared against [Capacity](term:inventory-capacity) rather than against [Available](term:inventory-available), so it asks whether you can still cover the stock you hold — not merely how many tags are in the drawer. It can be set as a flat number of tags or as a percentage, and the percentage resolves against your [uncertified demand](term:inventory-uncertified-products), so a moving target as your stock grows. Where no threshold has been set the row is only flagged once Capacity actually reaches zero or goes negative, which is a floor rather than a warning.",
    "seeAlso": [
      "inventory-capacity",
      "inventory-available"
    ]
  },
  {
    "id": "inventory-uncertified-products",
    "term": "Uncertified products",
    "aliases": [
      "uncertified",
      "uncertified demand"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "In-stock product units assigned to this tag that still have no certificate. It counts units rather than products, and it is this tag's demand.",
    "long": "The word to hold onto is units. A product with forty in stock and ten certified contributes thirty here, not one — counting it as a single product would understate a large line as badly as a small one and make the figure useless for ordering. Every other column on this table counts tags, so this is the one that counts the other side of the equation: what the tags are for. It is the number subtracted from [Available](term:inventory-available) to give [Capacity](term:inventory-capacity).",
    "seeAlso": [
      "inventory-capacity",
      "inventory-units-in-stock",
      "certificate"
    ]
  },
  {
    "id": "inventory-units-in-stock",
    "term": "Units in stock",
    "aliases": [
      "product stock"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Total stock across the products this tag is assigned to. Uncertified products is the part of it still waiting for a certificate.",
    "long": "This is the only column on the table that counts products rather than tags, which is why it is hidden by default — on a table about tag supply it is context, not a figure to act on. Its use is as a denominator: [Uncertified products](term:inventory-uncertified-products) read against it tells you how far through certifying a line you are, where the same shortfall means something different against a run of fifty and a run of five thousand.",
    "seeAlso": [
      "inventory-uncertified-products",
      "product"
    ]
  },
  {
    "id": "invoice",
    "term": "Invoice",
    "aliases": [
      "bill"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A bill Vera has issued you, for a subscription period or for an order of tags. Each one shows what it covers and whether it is paid.",
    "long": "Invoices carry a type as well as a payment state, and the two answer different questions: the type says whether this is your recurring [plan](term:plan) or a one-off purchase of [tags](term:tag), the payment state says where it has got to. An invoice for a [subscription](term:subscription) period includes any [activations](term:activation) that period billed for, which is why the amount is not simply your plan price.",
    "seeAlso": [
      "subscription",
      "order",
      "next-invoice-total"
    ]
  },
  {
    "id": "items-per-owner",
    "term": "Items per owner",
    "aliases": [
      "certificates per owner"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average number of certificates held per owner. It counts items, not designs — six identical watches are six items.",
    "long": "An [owner](term:owner) is a person, and this counts what they hold rather than how many different [products](term:product) they hold, so a collector with six of the same watch reads as six.\n\nCertificates that have not yet reached a first owner still sit with the brand and are not part of the average. It is a mean rather than a median, so one large holder moves it noticeably on a small owner base.",
    "formula": "Certificates with an owner ÷ distinct owners.",
    "seeAlso": [
      "owner",
      "certificate",
      "product"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "lead",
    "term": "Lead",
    "aliases": [
      "leads"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Someone who scanned one of your certificates and gave an email that is not one of your owners' — a stranger showing interest, not an existing customer.",
    "long": "Vera identifies a lead by the email collected at the scan, so a scan with no email is not counted and one person using two addresses counts twice. The test is membership: the address is compared against the owners already linked to your company, and anyone not on that list is a lead. The same test backs the Leads list, the Top Influencers leaderboard on Analytics and Ask Vera, so the three never disagree about who counts. It records exposure rather than intent — a lead scanned something of yours, which is not the same as having been referred to you.",
    "seeAlso": [
      "owner",
      "scan",
      "certificate"
    ]
  },
  {
    "id": "leads-captured",
    "term": "Leads captured",
    "aliases": [
      "captured leads"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many distinct people gave you an email through a scan in the period, counting only those who are not already one of your owners.",
    "long": "A [lead](term:lead) is identified by the email left at the scan, so a scan with no email is not counted and one person using two addresses counts twice. The deduplication matches the Leads list exactly, which is what stops this figure and that page disagreeing about who counts.\n\nIt records exposure rather than intent: someone scanned something of yours, which is not the same as having asked to hear from you.",
    "formula": "Distinct lowercased emails collected by scans in range, excluding your existing owners.",
    "seeAlso": [
      "lead",
      "owner",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "next-invoice-total",
    "term": "Next invoice total",
    "aliases": [
      "upcoming invoice",
      "next invoice"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "your plan fee plus the activations billed so far this period, as currently drafted",
    "unit": "USD",
    "short": "What your next invoice is drafted at right now: your plan fee plus this period's billable activations. It rises as the period runs.",
    "long": "This is a draft rather than a forecast — it reflects what has already happened this period, not a projection of the whole of it, so expect it to climb until the period closes. [Activations](term:activation) covered by your [included allowance](term:included-activations) or your [rollover](term:rollover) balance are absent from it entirely rather than shown and credited, because a covered activation is never billed in the first place. Any tax or discount is applied at the point the invoice is issued.",
    "seeAlso": [
      "activation-total-cost",
      "included-activations",
      "rollover"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "order",
    "term": "Order",
    "aliases": [
      "tag order",
      "tag shop order"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A purchase you place with Vera for physical tags. It is fulfilled as one or more boxes, and each box arrives as a package.",
    "long": "An order is for TAGS, not for anything your own customers buy. Vera does not hold your storefront's orders, so if you connect a store, what arrives in Vera is its [products](term:product); the sales stay where they are. An order's own state comes from the fulfilment side and is separate from whether it has been paid for, which is why the two are shown as different columns rather than one.",
    "seeAlso": [
      "package",
      "invoice"
    ]
  },
  {
    "id": "order-status-fulfilled",
    "term": "Fulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Every box in this order has shipped. It says nothing about whether they have arrived, which the tracking does.",
    "seeAlso": [
      "order"
    ]
  },
  {
    "id": "order-status-partially-fulfilled",
    "term": "Partially fulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Some of this order has shipped and some has not. A large order can leave in more than one box, and each box ships when it is ready.",
    "long": "Partially fulfilled is a normal state rather than a problem: splitting a large [order](term:order) lets the first boxes reach you while the rest are still being [encoded](term:encode). Tracking is per box, so an order in this state can have one number that is moving and another that does not exist yet.",
    "seeAlso": [
      "order",
      "package"
    ]
  },
  {
    "id": "order-status-unfulfilled",
    "term": "Unfulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Nothing in this order has shipped yet. It is the state every order starts in, including one already paid for.",
    "seeAlso": [
      "order"
    ]
  },
  {
    "id": "owner",
    "term": "Owner",
    "aliases": [
      "owners",
      "certificate owner"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The person a certificate currently belongs to, which changes only through a completed transfer.",
    "long": "An owner is a person, not necessarily an account: ownership is recorded when an item changes hands, so a [certificate](term:certificate) can have an owner who has never signed in — for instance one created from a store's customer list. Because ownership only moves by [transfer](term:transfer), an owner is never edited in place, and every previous owner stays on the item's [provenance](term:provenance). A certificate that has not yet reached its first owner still sits with the brand.",
    "seeAlso": [
      "transfer",
      "certificate",
      "provenance"
    ]
  },
  {
    "id": "package",
    "term": "Package",
    "aliases": [
      "packages",
      "box of tags"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A box of tags Vera has filled and linked to your order. Assigning it to one of your products creates a certificate for every tag in it.",
    "long": "A package does not exist when you place an order. It comes into being once Vera has [encoded](term:encode) the tags, boxed them and linked that box to your order, which is when it appears on the order ready to be assigned. Assigning is whole-box and one product, and the point of it is what happens at the other end: every tag already stands for a [certificate](term:certificate), so whoever applies them needs no Vera account, no app and no training. A package can be reassigned to a different product while none of its certificates has been activated, and is frozen once any of them has. The package is also the unit your stock moves in: scanning one in on arrival is what turns its tags from [Incoming](term:inventory-incoming) into [Available](term:inventory-available).",
    "seeAlso": [
      "tag",
      "certificate",
      "encode",
      "activation-trigger",
      "inventory-available"
    ],
    "docs": [
      "tags/assign-product-to-package"
    ]
  },
  {
    "id": "parent-certificate",
    "term": "Parent certificate",
    "aliases": [
      "parent certificates",
      "group parent",
      "tagless parent"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The certificate that stands for the whole group. It carries no tag of its own, so it is reached through a member's tag rather than scanned directly.",
    "long": "A parent is created for you whenever a group is created, and it is tagless by design: the group's tags are on its parts, so there is nothing to fix a separate tag to. On an indivisible group the parent is the sellable unit, which is why it must be given a product of its own. On a divisible group it is only a container, because billing and inventory already follow each part's own product, so its product is optional. A parent is otherwise an ordinary certificate: it activates, it carries an activation date, and it has its own page. When the last member is removed the parent is archived rather than deleted, so the record that the group existed survives.",
    "seeAlso": [
      "certificate-group",
      "child-certificate",
      "group-member",
      "split-group"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "permission",
    "term": "Permission",
    "aliases": [
      "permissions"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A single thing a user may do, such as creating certificates. Permissions are granted through roles, never to a person directly.",
    "long": "Each permission names one capability on one area, which is what makes access reviewable: you can read a [role](term:role) as a list of the things it allows rather than inferring it from a job title. Where a user lacks one, the control it gates usually stays visible and disabled, with the missing permission named — so the path to getting access is discoverable rather than a dead end. The check also runs on the server, so hiding or showing a button is a convenience and never the boundary itself.",
    "seeAlso": [
      "role"
    ]
  },
  {
    "id": "plan",
    "term": "Plan",
    "aliases": [
      "tier",
      "pricing tier"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The named tier your subscription is on. It sets your monthly price, your included activations, and which features are switched on.",
    "long": "Plans differ along four axes: how many [activations are included](term:included-activations) each month, how much history and analysis you can see, how far you can put your own brand in front of Vera's, and how finely you can divide access across a team. A plan that is no longer sold is marked retired; a company already on one keeps it until it moves.",
    "seeAlso": [
      "subscription",
      "included-activations",
      "white-label",
      "seat"
    ]
  },
  {
    "id": "product",
    "term": "Product",
    "aliases": [
      "products"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The model an item is made to — the design a brand makes many of. Every individual unit of it has its own certificate.",
    "long": "Product and [certificate](term:certificate) are the pair people most often read as the same thing, and the difference is one of counting. A product is the design: one entry, however many are made. A certificate is one physical unit of it, so a hundred handbags made to the same design are one product and a hundred certificates. That is why the product details on a certificate — the description, the images, the manufacture date — look the same on every unit, while the owner, the [provenance](term:provenance) and the scan history belong to the single item in your hands.",
    "seeAlso": [
      "certificate",
      "brand"
    ]
  },
  {
    "id": "product-lifespan",
    "term": "Product lifespan",
    "aliases": [
      "observed lifespan"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time from an item first existing to its most recent scan. Measured from the manufacture date where there is one, and from certificate creation where there is not.",
    "long": "⚠️ Not a durability figure, and the difference matters. Vera has no record of an item being discarded, so anything still in circulation keeps adding to this number every time it is scanned — for a healthy catalogue the figure grows over time, and that is the measure working rather than drifting.\n\n**It is measured from two different starting points, and the card says how many fall on each side.** A [product](term:product) with a manufacture date is measured from that date. One without is measured from when its [certificate](term:certificate) was created, which is when the item entered Vera rather than when it was made. Those answer different questions — how old the item is, versus how long it has been on the system — so read the line under the figure before comparing the number to anything.\n\nThe second starting point runs low. Items added to Vera in bulk long after they were made all carry the date they were imported, so the oldest pieces get the newest starting point and the figure understates them. Filling in manufacture dates on your products moves them onto the first basis and makes the number truer.",
    "formula": "Median days from the manufacture date — or, where there is none, the earliest certificate creation date — to the latest scan, over items scanned in the period.",
    "seeAlso": [
      "product",
      "scan",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "provenance",
    "term": "Provenance",
    "aliases": [
      "chain of ownership",
      "history"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The full ownership history of an item, from the brand through every completed transfer.",
    "long": "Provenance reads newest-first: one entry per completed [transfer](term:transfer), each with its date and how that owner acquired the item, ending at the [brand](term:brand) where the certificate was created. Every entry also records how the handover was proven, because this is the part of a certificate a resale buyer actually reads and \"it happened\" is weaker evidence than \"the recipient accepted it and scanned the tag\". Only completed hops are entries — a cancelled or still-pending transfer never appears, and nothing is removed once it does.",
    "seeAlso": [
      "owner",
      "transfer",
      "certificate"
    ]
  },
  {
    "id": "reader",
    "term": "Reader",
    "aliases": [
      "vera reader",
      "hardware reader",
      "desk reader"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Vera's desk hardware for encoding and checking tags in bulk, faster than tapping each one with a phone.",
    "long": "A reader plugs into the computer running Vera and is reached through the Vera Reader Helper, so the platform can [encode](term:encode) or check a [tag](term:tag) as quickly as you can place one on it. A phone does the same work one tap at a time, which is right for a spot check and wrong for a box of five hundred. When the platform says the reader is not connected, either nothing is plugged in or the helper is not running.",
    "seeAlso": [
      "encode",
      "tag",
      "scan"
    ]
  },
  {
    "id": "recovery-code",
    "term": "Recovery code",
    "aliases": [
      "recovery codes",
      "backup code",
      "backup codes"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A single-use code that signs you in when you cannot reach your authenticator app or phone. Shown once when you turn on two-factor authentication.",
    "long": "Recovery codes are your way back in if your phone is lost, broken or replaced. Save them somewhere you can reach without that phone — a password manager, or printed and put away.\n\nThey are shown exactly once and cannot be retrieved later: Vera stores only a one-way hash, so nobody, including support, can read them back to you. Each code works once. If you think they have been seen by someone else, or you simply run low, you can replace the whole set with a fresh one — which immediately invalidates every code from the old set.",
    "seeAlso": [
      "two-factor-authentication",
      "authenticator-app"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "remove-from-group",
    "term": "Remove from group",
    "aliases": [
      "remove a certificate from a group",
      "unlink from group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Taking one member out of a group. The group carries on with whatever members remain, even if only one is left.",
    "long": "Removing is the single-member counterpart to splitting, and it only ever detaches what you asked it to. A group with one child left is a normal state, not a broken one, so swapping a part out and a replacement in never destroys the group in between. Removing the last child does empty the group, which dissolves it and archives the parent, and the confirmation says so before you commit. On an indivisible group the action is available only while every member is still unactivated and unowned. Removing a child that is already active is a billable event, charged as a new activation, unless it is the last one leaving, which carries the group's original charge instead.",
    "seeAlso": [
      "split-group",
      "certificate-group",
      "group-member",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "repeat-scan-rate",
    "term": "Repeat scan rate",
    "aliases": [
      "repeat rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Of the items scanned at all in the period, the share scanned more than once. Counted over items, not over scans.",
    "long": "The denominator is ITEMS, which is the usual misreading: a hundred scans of one item is one item scanned, and reads as 100%. The card always prints its base for that reason — a rate over two items is arithmetically correct and tells you nothing.\n\n⚠️ The same rate appears on the Scans by country map and the two will NOT reconcile, by design. There it is computed inside each country — of the items scanned in France, how many were scanned in France more than once — so an item scanned once in France and once in Japan is a repeat here and a single scan in both countries. Neither figure is wrong, and no aggregation of the country ones produces this one.",
    "formula": "Certificates scanned two or more times ÷ certificates scanned at all, in range.",
    "seeAlso": [
      "scan",
      "certificate",
      "countries-reached"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "reported-lost",
    "term": "Reported lost",
    "aliases": [
      "lost reports"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many certificates were reported lost during the period. It counts the report as a dated event, so it moves with the date filter.",
    "long": "Counts the report rather than the number of items currently in a lost state — a status has no date, so only the event can be scoped to a period.\n\nLost and [stolen](term:certificate-status-stolen) are recorded separately because they mean different things to someone reading an item's history later: one is a misplacement, the other an allegation. [Lost](term:certificate-status-lost) is what the state does to a certificate.",
    "formula": "Count of lost reports logged in range.",
    "seeAlso": [
      "certificate-status-lost",
      "reported-stolen",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "reported-stolen",
    "term": "Reported stolen",
    "aliases": [
      "stolen reports"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many certificates were reported stolen during the period. It counts the report as a dated event, so it moves with the date filter.",
    "long": "Counts the report, not the number of items sitting in a stolen state right now. Those are different figures, and only this one can be asked about a period at all — a status carries no date, so counting statuses would produce a number that ignored the date filter while sitting beside figures that honour it.\n\nSo a certificate reported stolen last year and never recovered appears in last year's figure and not in this month's. [Stolen](term:certificate-status-stolen) is what the state itself does to a certificate.",
    "formula": "Count of stolen reports logged in range.",
    "seeAlso": [
      "certificate-status-stolen",
      "reported-lost",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "resales",
    "term": "Resales",
    "aliases": [
      "completed resales"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many ownership transfers completed as a sale in the period. Gifts and the brand's first handoff to an owner are not counted.",
    "long": "A resale is a [transfer](term:transfer) the sender marked as a [sale](term:sell), so this reflects how items were declared to have changed hands rather than any payment Vera observed. [Gifts](term:gift) are excluded, as is the first handoff out of the brand — that one is the original sale, not a resale of it.\n\nNot every resale carries a declared price; [Average resale price](term:average-resale-price) covers what happens to the ones that do not.",
    "formula": "Count of completed sale transfers in range.",
    "seeAlso": [
      "sell",
      "gift",
      "transfer",
      "average-resale-price",
      "resales-per-resold-item"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "resales-per-resold-item",
    "term": "Resales per resold item",
    "aliases": [
      "resales per item"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many times an item that has been resold changes hands on average. Items never resold are excluded, so this measures resale behaviour.",
    "long": "The denominator is deliberately items that HAVE resold rather than the whole catalogue. That is what separates \"how lively is the secondary market for these\" from \"how much of my catalogue resells at all\" — dividing by every certificate would answer the second question under the first one's name.\n\nA figure near 1 means resold items typically move once. Well above 1 means the same items keep circulating.",
    "formula": "Completed resales ÷ distinct items with at least one resale.",
    "seeAlso": [
      "resales",
      "average-resale-price",
      "sell"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "return-fraud-prevented",
    "term": "Return fraud prevented",
    "aliases": [
      "return fraud value"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The part of your own stated annual return-fraud loss that Vera eliminates, prorated to the period and scaled by how much of your catalogue is certified.",
    "long": "Rests on one figure you supply — your annual return-fraud loss — and the card asks for it rather than assuming one. Until you enter it the figure is withheld instead of guessed.\n\nWhat it is NOT is an estimate of detection. A certified item either verifies as the one that was sold or it does not, so on covered stock the check is deterministic rather than probabilistic. That is exactly why it is scaled by [certificate coverage](term:certificate-coverage) instead of claimed in full: nothing was there to verify on uncertified goods, and claiming to have stopped fraud on items Vera never touched is the kind of number a brand notices.\n\nIt counts toward [Value delivered](term:value-delivered), and it is the one input-driven metric where supplying the number actually changes the figure.",
    "formula": "Your stated annual return-fraud loss, prorated to the period, × certificate coverage.",
    "seeAlso": [
      "certificate-coverage",
      "value-delivered",
      "return-on-vera",
      "verify"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "return-on-vera",
    "term": "Return on Vera",
    "aliases": [
      "roi multiple",
      "return multiple"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Value delivered divided by what you have spent with Vera, over your whole history. The headline figure on Analytics.",
    "long": "⚠️ Measured over ALL TIME, not over the page's date range, and that is forced by the denominator rather than chosen: spend is settled invoices, which land in lumps on invoice dates, so a thirty-day window falling between two invoices divides by zero. The page's date picker sits BELOW this card for that reason — everything beneath it moves with the range, this does not.\n\nIt is calculated once, as one sum of [value delivered](term:value-delivered) over one [spend](term:vera-spend) figure, rather than per card. Per-card multiples are not additive, and a reader shown six of them will add them.\n\nThe page's other filters — owner, product, certificate, location — do still apply, and they narrow the numerator while the denominator stays company-wide. So filtering to a single product reports that product's value against your entire bill.\n\n⚠️ One more caveat if your [spend](term:vera-spend) settled in a currency other than your reporting one: the two figures are divided as numbers, so the multiple mixes currencies rather than converting between them. Read it as approximate whenever the hero shows two different symbols.",
    "formula": "Value delivered ÷ Spend with Vera, both over the company's whole history.",
    "seeAlso": [
      "value-delivered",
      "vera-spend",
      "invoice",
      "subscription"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "role",
    "term": "Role",
    "aliases": [
      "roles"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A named set of permissions you assign to a user. Editing the role changes what everyone holding it can do.",
    "long": "Roles exist so access is granted by job rather than by person: you decide once what Finance may do, and every finance hire inherits it. The consequence worth remembering is that a role is shared — widening one to unblock a single person widens it for everyone who holds it, which is usually the moment to create a role instead of editing one. A user holds exactly one role at a time, so the role is the whole answer to what they can do.",
    "seeAlso": [
      "permission"
    ]
  },
  {
    "id": "rollover",
    "term": "Rollover",
    "aliases": [
      "activation rollover",
      "carry over"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Included activations you do not use are added to your balance and carried forward. There is no cap, and they do not expire while you stay subscribed.",
    "long": "Rollover is what stops an allowance from punishing an uneven year: a quiet quarter is not lost, it is banked against the busy one. The balance is a single pool — unused monthly [allowance](term:included-activations) and any activations Vera [grants](term:granted-activations) you accumulate together, with nothing expiring first. It is spent before you are charged, so an activation covered by the balance is not billed at all rather than billed and credited back, which is why it never appears as a line on an [invoice](term:invoice).",
    "seeAlso": [
      "included-activations",
      "granted-activations",
      "activation"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "scan",
    "term": "Scan",
    "aliases": [
      "scans",
      "tap"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "One read of a tag by a phone, recorded with its time and place so owners can see where an item has been checked.",
    "long": "Every read is written down, including the ones that fail, so a [certificate](term:certificate)'s scan list is where the exceptions show up next to the ordinary taps. That is why a scan carries an outcome of its own rather than being a simple count: what the [tag](term:tag) proved varies with the tag and with the tap. Opening a certificate's web link without tapping anything is also recorded here, but marked as a link event, so the two kinds of evidence are never added together.",
    "seeAlso": [
      "tag",
      "certificate",
      "scan-status-verified"
    ],
    "docs": [
      "certificates/certificate-scans",
      "integrations/settings-installation"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "scan-location-accuracy",
    "term": "Scan location accuracy",
    "aliases": [
      "location resolution"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of scans where a location could actually be resolved. A data-quality measure about your scan records. Not computed yet.",
    "long": "A scan carries a location only when the device and the network gave one up, so this says how complete the geography on this page is — not anything about how your items are doing.\n\nIt is the figure to check first when [Countries reached](term:countries-reached) or the map looks thinner than your scan count suggests it should. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Scans with a resolved location ÷ all scans in range.",
    "seeAlso": [
      "countries-reached",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "scan-status-connected",
    "term": "Connected",
    "aliases": [
      "connected scan",
      "connect tag scan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A tag was tapped and opened the certificate. The tag is sealed, but carries no per-tap proof, so a scan cannot tell it apart from a copy of its address.",
    "long": "Connect tags are sealed against rewriting but cannot answer a security challenge, so a scan confirms the certificate was opened by a [tag](term:tag) rather than confirming which tag. This is the expected outcome for these tags, not a failure: it is reported separately from [Verified](term:scan-status-verified) so a tap that proved nothing about the chip is never recorded as one that did, and separately from [Invalid](term:scan-status-invalid) so a legitimate tap on a tag you chose is never logged as suspicious.",
    "seeAlso": [
      "scan-status-verified",
      "scan-status-invalid",
      "tag"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-invalid",
    "term": "Invalid",
    "aliases": [
      "invalid scan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A tag was tapped but did not verify. It may be a copy of a real tag, or a genuine tag that is damaged or misprogrammed.",
    "long": "Invalid is evidence, not a verdict. Something was physically tapped and its proof did not check out, which is consistent with a copied [tag](term:tag) but equally with a genuine one that is damaged, was programmed wrong, or was read badly. Read a single invalid scan as something to look into rather than a confirmed counterfeit, and read it against the certificate's other [scans](term:scan). It is ranked below states that are confirmed rather than inferred, and it is a different kind of thing from the link statuses, where nothing was tapped at all.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-verified",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-invalid-link",
    "term": "Invalid link",
    "aliases": [
      "invalid link",
      "malformed link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A malformed certificate link was opened. No tag was tapped, so nothing about a physical item was checked.",
    "long": "The address itself did not parse — truncated by a messaging app, mistyped, or mangled by a link preview — so there was nothing to check against. Like the other link statuses it involves no tap, so it is silent about the item; unlike [Suspicious link](term:scan-status-suspicious-link), there is no sign of anyone trying to forge anything. Despite the name it is unrelated to an [Invalid](term:scan-status-invalid) scan, which is a real tag that failed verification. It is logged for completeness rather than as something to act on.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-stale-link",
      "scan-status-invalid"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-opened",
    "term": "Opened",
    "aliases": [
      "tampered",
      "tamper",
      "opened seal"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "The tag reports that its seal was opened. On a closure or seal tag that is the expected outcome once the owner opens the item.",
    "long": "A fact about the seal, not a problem with the [scan](term:scan). A scan can verify perfectly and still report an opened seal, which is why such a scan keeps the normal [verified](term:scan-status-verified) marker rather than being flagged. Only a scan that failed verification is [Invalid](term:scan-status-invalid).\n\nThe certificate page carries the same word as a pill beside the certificate's status once any scan has reported it, so a certificate can be Active and Opened at the same time. What the hardware detects is that the seal's circuit was broken. It cannot tell who broke it or why, and the ordinary cause is the owner opening the item they bought. Read it alongside the rest of the scan history rather than as evidence of interference.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-verified"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-stale-link",
    "term": "Stale link",
    "aliases": [
      "stale link",
      "expired link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "An expired certificate link was reopened. Usually harmless, such as an old link in a message or a stale browser tab.",
    "long": "[Certificate](term:certificate) links are short-lived by design, so an old one stops working rather than staying open indefinitely. Reopening one lands here, and it is nearly always innocent: a link forwarded in a message, a tab left open overnight, a page revisited from history. The distinction from [Suspicious link](term:scan-status-suspicious-link) is what happened to the token — one simply aged out, the other did not hold up to checking.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-suspicious-link",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-suspicious-link",
    "term": "Suspicious link",
    "aliases": [
      "suspicious link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Someone opened this certificate's link using a forged or mismatched token. No tag was tapped, so this is probing rather than a scan.",
    "long": "The three link statuses all describe someone opening a certificate's web address rather than tapping its [tag](term:tag), so none of them says anything about the physical item. This is the one worth attention: the address carried a token whose signature did not hold up, or one issued for a different purpose or a different certificate — a pattern that looks like someone testing what the links will accept, rather than a person following an [old link](term:scan-status-stale-link). It is recorded against the certificate so that probing is visible.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-stale-link",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-verified",
    "term": "Verified",
    "aliases": [
      "verified scan"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A tag was tapped and confirmed genuine. This is the normal outcome, so it carries no badge in the scan list.",
    "long": "The chip answered a challenge only that chip could answer, so the tap identified the physical [tag](term:tag) rather than merely proving that a link opened. It is the overwhelmingly common outcome, which is why it is the one status with no chip: badging every ordinary row would carry no information and bury the exceptions, so the absence of a badge is itself the signal. Only tags whose family can prove identity on each tap can reach this state — a sealed tag with no per-tap proof is reported as Connected instead.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-invalid",
      "scan-status-connected",
      "scan-status-opened"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "seat",
    "term": "Seat",
    "kind": "term",
    "audience": "brand",
    "short": "One person on your team with their own login. Your plan is sold with a set number of them, and you cannot invite past that number.",
    "long": "A seat is a person, not a [role](term:role): someone with several responsibilities still occupies one, and a role can be held by any number of people. Both people who have accepted and people you have invited but who have not signed in yet occupy a seat — otherwise a single batch of invites could take you well past your [plan](term:plan) without ever appearing to. Removing or archiving someone frees theirs immediately. If you are already above your plan's number, nobody is removed and nothing stops working; you simply cannot add anyone else until you upgrade or free a seat.",
    "seeAlso": [
      "plan",
      "role",
      "seats-used"
    ],
    "aliases": [
      "seats",
      "team seat",
      "user seat"
    ]
  },
  {
    "id": "seats-over-limit",
    "term": "Over your seat limit",
    "aliases": [
      "over limit",
      "over seat limit",
      "more seats than my plan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Your team is larger than the number of seats your plan sells. Everyone keeps working. You cannot invite anyone new until you free a seat or upgrade.",
    "long": "Reads as something like “8 of 5 seats”, which looks like an error and is not one. It happens when [seat](term:seat) limits began applying to an account that was already larger than its [plan](term:plan), or when a plan changes to one that sells fewer seats. Nobody is removed, suspended or signed out to make the arithmetic work — the only thing that changes is that new invites are refused. Archiving or removing people brings the figure down; it is not held against you in any other way, and there is no separate charge for the extra people.",
    "seeAlso": [
      "seat",
      "seats-used",
      "plan"
    ]
  },
  {
    "id": "seats-used",
    "term": "Seats used",
    "aliases": [
      "seats in use",
      "seat usage"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "people on your team who have accepted + people you have invited who have not",
    "unit": "seats",
    "short": "How many of your plan's seats are taken. Counts everyone on your team plus anyone invited who has not signed in yet.",
    "long": "Shown on the Users page as “4 of 5 seats”. Pending invites count from the moment you send them, so the figure moves when you invite rather than when the person accepts — that is what stops a single batch of invites carrying you past your [plan](term:plan). Archived people do not count, which is why archiving is the quickest way to free one. Nor do Vera staff attached to your account, so the figure can read one lower than the list of names beneath it. Withdrawing an invite frees its [seat](term:seat) as soon as you remove the row.",
    "seeAlso": [
      "seat",
      "plan",
      "subscription"
    ]
  },
  {
    "id": "sell",
    "term": "Sell",
    "aliases": [
      "selling",
      "sale",
      "resell",
      "resale"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to a buyer as part of a sale, recording the price alongside the handover so it becomes part of the item's history.",
    "long": "Selling differs from [gifting](term:gift) in exactly one respect that outlives the handover: a price is recorded. That figure is what makes a [provenance](term:provenance) useful to a later buyer, since a chain of documented sales is evidence of what an item has actually been worth rather than what it is asked for. Selling through Vera is not yet available in the app; until it is, an item sold privately is handed over as a [transfer](term:transfer), which moves ownership correctly but records no price.",
    "seeAlso": [
      "gift",
      "transfer",
      "provenance"
    ]
  },
  {
    "id": "sibling-certificate",
    "term": "Sibling certificate",
    "aliases": [
      "sibling",
      "siblings",
      "sibling certificates",
      "related certificate",
      "related certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Another child of the same parent. Siblings are peers in one group, not parts of each other.",
    "long": "The other children of a certificate's own parent. A sibling is a peer, which is what separates it from a parent (above it) and a child (below it) — nothing contains a sibling, so no hierarchy is drawn between them. Whether siblings can be sold apart is not a property of the sibling relationship but of the group holding them: in a [divisible group](term:divisible-group) each sibling is independently sellable, and in an [indivisible group](term:indivisible-group) they go live and sell as one unit.",
    "seeAlso": [
      "child-certificate",
      "parent-certificate",
      "certificate-group",
      "divisible-group",
      "indivisible-group"
    ]
  },
  {
    "id": "split-group",
    "term": "Split group",
    "aliases": [
      "split a group",
      "splitting a group",
      "dissolve a group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Breaking a whole group apart. Every member becomes a standalone certificate, and the parent certificate it leaves empty is archived.",
    "long": "Splitting destroys no record. It severs a relationship: every former child carries on as an ordinary standalone certificate, and the parent, now holding nothing, is archived so it stops listing as an empty group. The action is offered on the parent, and it is always available on a divisible group. On an indivisible group it is available only while every member is still unactivated and unowned, because after that the group is permanent. Separating members that are already active is a billable event: one of them carries the group's original charge and the rest are charged as new activations.",
    "seeAlso": [
      "remove-from-group",
      "certificate-group",
      "indivisible-group",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "studio",
    "term": "Studio",
    "aliases": [
      "branding editor"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The editor where you set how your certificates look — theme, logo, colours and layout — without writing any code.",
    "long": "Studio styles the page a customer reaches when they tap an item, which for most brands is the only Vera surface a customer ever sees. It changes presentation and nothing else: no wording of the record, no ownership, no [provenance](term:provenance). Changes apply to every [certificate](term:certificate) at once rather than being versioned per item, so what a customer sees is always the current design, including on certificates issued long before it.",
    "seeAlso": [
      "certificate",
      "installation"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-primary-color",
    "term": "Primary color",
    "aliases": [
      "primary colour",
      "studio primary color"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Your main brand colour. It sets headings and primary buttons, but also field focus outlines, the pulsing rings on the scan screen, menu highlights and count badges.",
    "long": "Studio presents it as one colour, but the customer app derives an eleven-step scale from it and reads that scale in roughly a hundred places — so this control reaches every screen a customer can land on, not only the ones with a button. Page titles and section headings, primary buttons, the outline and soft glow when someone taps into a field, the row under the pointer in any menu or picker, the pulsing rings on the tag-[scan](term:scan) screen, the success tick after a claim or transfer, and the number badge on Filter and Sort all come from here.\n\nIn email it means far less: it fills buttons, and nothing else. Email headings follow your [text colour](term:studio-text-color) instead. And a recipient reading in dark mode sees none of your colours — the message switches to a fixed dark palette that overrides this, your text colour, your [border colour](term:studio-border-color) and your [background colour](term:studio-background-color). Fonts, [corner rounding](term:studio-border-radius) and your logo survive both appearances.",
    "seeAlso": [
      "studio",
      "studio-text-color",
      "studio-background-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-border-color",
    "term": "Border color",
    "aliases": [
      "border colour",
      "divider color",
      "divider colour"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The colour of dividers and panel borders on your certificate pages, and of the header rule, footer rule and divider lines in the emails your customers receive.",
    "long": "This is the one control whose email half has always been the louder one. In email it draws the rule under the header, the rule above the footer, the dividers between sections and the border around a verification code — and that has worked since the token existed. In the app it draws dividers and the outline of bordered panels on the certificate page.\n\nIt does not affect disabled buttons, despite what this control claimed for a long time: a disabled button keeps its own colour at reduced opacity. Like every colour in [Studio](term:studio), it is discarded when the email is read in dark mode.",
    "seeAlso": [
      "studio",
      "studio-primary-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-text-color",
    "term": "Text color",
    "aliases": [
      "text colour",
      "body text color",
      "body text colour"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The colour of body and paragraph text across your certificate pages and your emails. Large headings follow your primary colour instead, unless you have not set one.",
    "long": "Beyond paragraphs it colours button labels at rest, the selected item in the navigation bar and the selected row in a menu. It is worth checking against your [background colour](term:studio-background-color): a pale text colour on a pale surface has produced genuinely unreadable text on this app before.\n\nIn email it colours body copy **and** headings — the split with [primary colour](term:studio-primary-color) that exists in the app does not exist there. Small muted sub-labels are a fixed grey in both surfaces and are not yours to set.",
    "seeAlso": [
      "studio",
      "studio-primary-color",
      "studio-background-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-background-color",
    "term": "Background color",
    "aliases": [
      "background colour",
      "page background"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The page surface behind your certificate pages, and the outer wall around your emails. Not the email card itself, which stays white in light mode.",
    "long": "In the app it is the surface behind the certificates list, the [certificate](term:certificate) page and the share page, and it decides whether the scan map draws in its light or dark style. It does not reach sign-in, forgot-password or the onboarding screens.\n\nCards, bottom sheets and toasts stay white on top of it, so a dark background gives you a dark page carrying white cards. That is a current limit of the theme rather than a setting you have missed. In email it paints only the wall around the message — the card itself stays white in light mode and near-black in dark, and neither is brandable.",
    "seeAlso": [
      "studio",
      "studio-text-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-title-font",
    "term": "Title font",
    "aliases": [
      "heading font"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The typeface for large headings on your certificate pages and for the headline in your emails. Small section captions use the body font, not this one.",
    "long": "It applies to the four largest heading sizes. The smallest heading — the short caption above a group of rows — is not included and takes the [body font](term:studio-body-font), which is worth knowing if you have chosen a display face and cannot find it where you expected.\n\nIn email it applies to the headline only; everything else is the body font. Fonts are one of the three things that survive a recipient reading in dark mode, along with [corner rounding](term:studio-border-radius) and your logo.",
    "seeAlso": [
      "studio",
      "studio-body-font"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-body-font",
    "term": "Body font",
    "aliases": [
      "paragraph font"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The typeface for everything that is not a large heading. Body copy, buttons, labels and navigation, across your certificate pages and your emails.",
    "long": "It is also the fallback for headings: leave the [title font](term:studio-title-font) unset and headings take this one, so a brand using a single typeface only has to set this.\n\nIn email it is loaded as a web font and applied to the whole message, which makes it the typographic choice that travels most reliably into an inbox — it survives dark mode, unlike every colour in [Studio](term:studio).",
    "seeAlso": [
      "studio",
      "studio-title-font"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-border-radius",
    "term": "Border radius",
    "aliases": [
      "corner radius",
      "corner rounding"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "How rounded your buttons are. Form fields follow the same setting but stop at 6px, so a very round value gives round buttons and only gently rounded fields.",
    "long": "Past buttons it rounds the top corners of bottom sheets, the navigation items, and menu panels at half the value you set. The cap on fields is deliberate: a 24px radius on a 48px-tall input deforms it, so a field takes the smaller of your value and 6px. Zero still means square everywhere, so choosing sharp corners gets you sharp corners.\n\nIn email it reaches buttons only — the card's own corners are fixed. It is one of the three things that survive dark mode in an inbox, along with your fonts and your logo.",
    "seeAlso": [
      "studio",
      "studio-primary-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "subdomain",
    "term": "Subdomain",
    "aliases": [
      "vera subdomain",
      "brand address"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Your address on Vera, in the form yourbrand.vera.inc. It is where certificates open when you have no installation of your own.",
    "long": "The subdomain is issued when your company is created and is the default destination for every [scan](term:scan), so [certificates](term:certificate) are reachable before you have integrated anything. Changing it is not cosmetic: tags already in customers' hands were [encoded](term:encode) pointing at the old address, and the address written to a tag is fixed at the moment it is encoded. Treat a rename as a public change of address rather than a setting, and expect to be asked to confirm it.",
    "seeAlso": [
      "installation",
      "certificate",
      "encode"
    ],
    "docs": [
      "integrations/settings-installation"
    ]
  },
  {
    "id": "subscription",
    "term": "Subscription",
    "kind": "term",
    "audience": "brand",
    "short": "Your recurring agreement with Vera. It sets which plan you are on, what it costs, and when the next period starts.",
    "long": "The subscription is the [plan](term:plan) you hold, not the whole of what you pay: [activations](term:activation) are billed on top of it, against the [allowance the plan includes](term:included-activations). Its state governs access as well as billing, so a subscription that has lapsed is worth resolving before it reaches the point where the platform stops opening.",
    "seeAlso": [
      "plan",
      "invoice",
      "included-activations"
    ]
  },
  {
    "id": "subscription-status-active",
    "term": "Active",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription is paid and running. This is the only state in which nothing about billing needs your attention.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-canceled",
    "term": "Canceled",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription has ended. Certificates already issued are unaffected; what stops is the plan and everything it included.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-incomplete",
    "term": "Incomplete",
    "kind": "status",
    "audience": "brand",
    "short": "The very first payment on a new subscription has not gone through yet. Nothing has started, and the card usually needs confirming.",
    "long": "Incomplete only ever describes a subscription's opening payment, which is why it is a different state from [Past due](term:subscription-status-past-due): there is no previous period that was paid for. If the first payment is never completed the subscription [expires](term:subscription-status-incomplete-expired) rather than lapsing, and starting again means a new subscription.",
    "seeAlso": [
      "subscription",
      "subscription-status-incomplete-expired"
    ]
  },
  {
    "id": "subscription-status-incomplete-expired",
    "term": "Incomplete expired",
    "kind": "status",
    "audience": "brand",
    "short": "A new subscription whose first payment was never completed in time. It never started, so there is nothing to resume.",
    "seeAlso": [
      "subscription",
      "subscription-status-incomplete"
    ]
  },
  {
    "id": "subscription-status-past-due",
    "term": "Past due",
    "kind": "status",
    "audience": "brand",
    "short": "A payment failed and is being retried. The subscription still works. Update the payment method before the retries run out.",
    "long": "Past due is the warning window, and it is the cheapest moment to fix the problem: nothing has been cut off, and a working card resolves it with no further action. If the retries are exhausted the [subscription](term:subscription) moves on to [Unpaid](term:subscription-status-unpaid), which is the state that costs you access.",
    "seeAlso": [
      "subscription",
      "subscription-status-unpaid"
    ]
  },
  {
    "id": "subscription-status-paused",
    "term": "Paused",
    "kind": "status",
    "audience": "brand",
    "short": "Collection has been stopped deliberately. The subscription is not cancelled and is not being charged while it stays this way.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-trialing",
    "term": "Trialing",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription is inside its trial period. It has not been charged yet, and it will convert to a paid period when the trial ends.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-unpaid",
    "term": "Unpaid",
    "kind": "status",
    "audience": "brand",
    "short": "Every retry of a failed payment has been exhausted. This is the end of the dunning window, not the beginning of it.",
    "seeAlso": [
      "subscription",
      "subscription-status-past-due"
    ]
  },
  {
    "id": "support-request",
    "term": "Support request",
    "aliases": [
      "ticket"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A message you raise with Vera. You choose what kind it is; the reply, and the state it moves through, come back on the same thread.",
    "long": "A request carries two separate things: a [TYPE](term:support-request-type), which you pick once when you raise it and which does not change, and a STATUS, which only Vera moves as the request is worked. Replying to a request that had been [resolved](term:support-status-resolved) reopens it rather than starting a new one, so a thread stays one conversation.",
    "seeAlso": [
      "support-request-type"
    ]
  },
  {
    "id": "support-request-type",
    "term": "Request type",
    "aliases": [
      "support type"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What kind of request this is: a question, something broken, or something you would like built. You choose it once and it does not change.",
    "long": "Type is a classification and status is a lifecycle, which is why they are shown differently: a square badge for the kind of thing it is, a rounded chip for where it has got to. A feature request can end up linked to a public roadmap item; a bug and a question cannot.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-error-reference",
    "term": "Error reference",
    "aliases": [
      "ref",
      "reference id"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A short code, eight letters and numbers, that names one failure. Quote it to Vera and we can find exactly what went wrong.",
    "long": "Every time something fails on Vera's side, the failure is recorded under its own reference. When you report a problem from the error message, the reference is attached for you, so you never need to copy it. It identifies the failure, not you or your data, and it is different every time.",
    "seeAlso": [
      "support-request",
      "support-automatic-report"
    ]
  },
  {
    "id": "support-automatic-report",
    "term": "Automatic report",
    "aliases": [
      "auto report",
      "automatic error report",
      "system-generated report"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A bug report the app files by itself when a page crashes or shows an error that looks like a fault on Vera's side, so Vera hears about it even if nobody writes in.",
    "long": "It carries only the facts of the failure: what was shown, what failed, its [reference](term:support-error-reference), the page, your browser and app version. Never what you typed. It does not appear in your request list unless you add your own words to it, which the crash message offers. If you write in about the same error instead, your [request](term:support-request) is linked to it.",
    "seeAlso": [
      "support-request",
      "support-error-reference"
    ]
  },
  {
    "id": "support-status-closed",
    "term": "Closed",
    "kind": "status",
    "audience": "brand",
    "short": "Filed away without a resolution to point at, usually because it was withdrawn or overtaken. Replying reopens it.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-in-progress",
    "term": "In progress",
    "kind": "status",
    "audience": "brand",
    "short": "Someone at Vera is working on it. Nothing is needed from you while it stays here.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-open",
    "term": "Open",
    "kind": "status",
    "audience": "brand",
    "short": "Raised and waiting on Vera. A request also returns here on its own if you reply to one that had been resolved or closed.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-resolved",
    "term": "Resolved",
    "kind": "status",
    "audience": "brand",
    "short": "Vera considers it answered or fixed. Replying reopens the same thread rather than starting a new request.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-waiting-on-you",
    "term": "Waiting on you",
    "aliases": [
      "waiting on customer"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Vera has replied and needs something back before it can go further. This is the one state where the request is stalled on you.",
    "long": "Vera staff see this same state described from their side, as waiting on the customer. It is one state with two readings, so that whoever is looking at it is told what THEY have to do.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "sync-mode",
    "term": "Sync mode",
    "aliases": [
      "product sync mode",
      "value sync"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether later edits in your store keep overwriting a product in Vera. Set per product: follow the store default, always sync, or never.",
    "long": "The question it answers is not how a product first arrives but what happens to it afterwards. Locked protects a product you have edited by hand in Vera, so a store update can never overwrite your wording; Always sync does the opposite and takes the store's values every time; Follow store default defers to the connection's own \"Sync value updates\" setting. A manual Sync now is an explicit instruction and runs whatever the mode says. The control appears only on [products](term:product) that came from a [connected store](term:integration).",
    "seeAlso": [
      "field-mapping"
    ],
    "docs": [
      "integrations/settings-field-mapping"
    ]
  },
  {
    "id": "tag",
    "term": "Tag",
    "aliases": [
      "tags",
      "nfc tag"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The Vera tag attached to an item, which a phone reads to open that item's certificate.",
    "long": "A tag carries no product information of its own. It holds its own identity, and Vera resolves that identity to whichever [certificate](term:certificate) the tag is paired with. The pairing is a record held by Vera rather than something written onto the tag, so moving a tag onto a different object does not move the certificate with it — the tag simply keeps opening the certificate it was paired to. Some tags can also prove on each tap that they are the original and not a copy, which is what separates a [verified](term:scan-status-verified) scan from one that only shows a link was opened.",
    "seeAlso": [
      "certificate",
      "encode",
      "scan",
      "package"
    ],
    "docs": [
      "integrations/settings-installation",
      "tags/assign-product-to-package"
    ]
  },
  {
    "id": "time-to-first-resale",
    "term": "Time to first resale",
    "aliases": [
      "days to first resale"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time, in days, from a certificate being created to the first time that item was resold. A median, over items that were resold.",
    "long": "Measured from creation rather than from the first sale, so it includes however long the item sat before reaching its first owner — Vera does not hold the date a brand sold it, and inventing one would make this figure look precise while being wrong.\n\nItems never resold are left out rather than counted as a very long wait. So it describes resale behaviour among items that resold, and says nothing about how many did not. [Gifts](term:gift) and the brand's first handoff to an owner are not resales.",
    "formula": "Median days from certificate creation to the item's first completed resale.",
    "seeAlso": [
      "resales",
      "sell",
      "certificate",
      "time-to-first-scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "time-to-first-scan",
    "term": "Time to first scan",
    "aliases": [
      "days to first scan"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical gap, in days, between a certificate being created and the first time it was scanned. A median, not an average.",
    "long": "A median rather than a mean, so one certificate that sat dormant for a year cannot drag the figure past every real observation — against a long tail of slow-moving stock, an average reports a typical wait that almost nothing actually experienced.\n\nCertificates never scanned are excluded rather than counted as an infinite wait. So this describes the items that did get scanned, and says nothing about how many did not.",
    "formula": "Median days from certificate creation to its first scan.",
    "seeAlso": [
      "certificate",
      "scan",
      "time-to-first-resale"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfer",
    "term": "Transfer",
    "aliases": [
      "transfers",
      "transfer out",
      "transfer in"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Moving a certificate from one owner to the next; it is pending until the receiving owner confirms.",
    "long": "A transfer is the only thing that changes a certificate's [owner](term:owner). The sender starts it and the item stays with them until the recipient accepts, so an unclaimed transfer can be cancelled and a mistyped address never costs anyone the item. How each one completed is recorded and shown on the [provenance](term:provenance): accepted from the emailed link, or accepted with a scan of the physical tag as well. For most brands, the first transfer out of the brand is also what makes the certificate [active](term:certificate-status-active).",
    "seeAlso": [
      "owner",
      "provenance",
      "activation-trigger"
    ]
  },
  {
    "id": "transfer-completion-rate",
    "term": "Transfer completion rate",
    "aliases": [
      "completion rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of ownership transfers started in the period that went on to complete. Not computed yet; the card shows a dash.",
    "long": "Both halves are counted by the date the transfer was STARTED, so this is one cohort split by where it got to rather than two independently scoped figures — which is what makes the ratio mean anything at all. One consequence worth knowing: a recent window understates the rate, because the transfers in it have had less time to be accepted.\n\n[Completed transfers](term:transfers-completed) and [In progress transfers](term:transfers-in-progress) are the two counts behind it, and both are live on the KPI row today.",
    "formula": "Completed transfers ÷ all transfers started in range.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfers-in-progress",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfer-completion-time",
    "term": "Transfer completion time",
    "aliases": [
      "time to complete transfer"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time, in days, from an ownership transfer being started to it being accepted. A median, so one stalled transfer cannot skew it.",
    "long": "⚠️ Only transfers that COMPLETED are in this figure, and only ones that completed after Vera began stamping a completion time. Historical rows were deliberately not backfilled with a guess, so the figure fills in as new transfers finish rather than starting out complete and wrong — a thin number early on is the ramp, not a fault.\n\nTransfers still waiting are excluded rather than counted as long ones, which means a period full of unaccepted transfers can show a short and healthy-looking time. Read it beside [In progress transfers](term:transfers-in-progress).",
    "formula": "Median days from transfer start to transfer completion, over transfers that completed.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfers-in-progress",
      "transfer-completion-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfers-completed",
    "term": "Completed transfers",
    "aliases": [
      "completed transfers count"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Transfers started in the period that have since completed. Counted by the date the transfer began, not the date it was accepted.",
    "long": "⚠️ The date range picks transfers by when they were STARTED, so this is not \"transfers completed in the period\". One begun last month and accepted this month is counted in last month; one begun this month and still unaccepted is not counted at all until it completes, at which point it appears in this period retroactively. That is what makes it comparable with [In progress transfers](term:transfers-in-progress) beside it — the two split one cohort of started transfers by where it has got to, rather than counting two differently scoped populations. A [transfer](term:transfer) completes only when the receiving owner confirms, so a request nobody opened is never counted here however long it has waited.",
    "formula": "Count of transfers created inside the date range whose status is now completed.",
    "seeAlso": [
      "transfer",
      "transfers-in-progress",
      "transfer-completion-rate",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfers-in-progress",
    "term": "In progress transfers",
    "aliases": [
      "in progress transfers count",
      "open transfers"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Transfers started in the period that are still open — sent, and not yet accepted, cancelled or expired.",
    "long": "The date range picks transfers by when they were started, but the status is read as it stands NOW — so this figure falls over time without the range changing, as the transfers in it are accepted and move into [Completed transfers](term:transfers-completed). The two therefore split one cohort rather than counting two, which is what lets them sit side by side. Nothing here says how long a transfer has been waiting; [Transfer completion time](term:transfer-completion-time) is the figure for that, and it only ever sees the ones that finished.",
    "formula": "Count of transfers created inside the date range whose status is still pending.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "two-factor-authentication",
    "term": "Two-factor authentication",
    "aliases": [
      "2fa",
      "two-factor",
      "two-step verification"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A second step when you sign in. After your password or emailed code, you enter a short code from an authenticator app or a text message.",
    "long": "One factor is something you know (a password, or a code sent to your email). Two-factor authentication adds something you have — your phone. Someone who learns your password still cannot sign in without it.\n\nIt is optional for most people and can be turned on from your security settings. Once it is on, every sign-in asks for the second code; there is no way to skip it, which is the point. If you lose access to your second factor, a recovery code gets you back in.",
    "seeAlso": [
      "authenticator-app",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "unattributed-invalid-scans",
    "term": "Unattributed invalid scans",
    "aliases": [
      "unattributed scans"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Rejected scans that matched no certificate at all — a tag presented to Vera that Vera never issued. Reported beside the invalid count, never inside it.",
    "long": "These are the highest-signal rejections there are: not a misread of your [tag](term:tag), but something claiming to be one. They cannot join [Invalid scans](term:invalid-scans) because every filter on that figure reaches through the link to a [certificate](term:certificate), which a certificate-less scan does not have — so they are counted separately rather than lost.\n\n⚠️ For the same reason the count is reported as zero, not estimated, whenever you filter the page by owner, product or certificate. A scan with no certificate cannot satisfy a condition about one, and the honest answer under that filter is that the figure is not addressable — not a count that quietly ignores the filter you set.",
    "formula": "Count of invalid scans in range carrying no certificate, scoped to your company.",
    "seeAlso": [
      "invalid-scans",
      "invalid-scan-rate",
      "tag",
      "scan-status-invalid"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "value-delivered",
    "term": "Value delivered",
    "aliases": [
      "roi value delivered"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The money value Vera can show it produced for you, added up across every metric with a measured figure behind it.",
    "long": "Only metrics Vera can actually MEASURE are added in. One it cannot compute is left out of the sum entirely rather than counted as zero, and the card reports how many sources went into the figure — so a modest total is visibly a small number of sources rather than a poor result.\n\nMetrics resting on a figure you supply, such as [Return fraud prevented](term:return-fraud-prevented), join the sum once you supply it and are excluded until then. The total never quietly assumes a number on your behalf, which means real value Vera cannot see understates this figure. That is the direction it should err in: it is the numerator of [Return on Vera](term:return-on-vera), and an overstated one would be the more flattering mistake.",
    "formula": "Sum of the currency metrics that contribute to return, over all time.",
    "seeAlso": [
      "return-on-vera",
      "vera-spend",
      "return-fraud-prevented",
      "counterfeit-deflections"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "vera-spend",
    "term": "Spend with Vera",
    "aliases": [
      "vera spend"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "What you have paid Vera — subscription plus billable activations — read from settled invoices across your whole history.",
    "long": "Read from your [invoices](term:invoice) rather than recomputed, so this is money that actually changed hands. Charges accrued since the last invoice are added on top and named separately in the breakdown, because they are real spend that simply has not been billed yet — leaving them out would make a company mid-billing-period look cheaper than it is and push [Return on Vera](term:return-on-vera) up.\n\nWhere the invoice history is incomplete for the range, the card says so rather than reporting a confident total. An understated denominator overstates the return, so silence there would flatter rather than merely mislead.\n\n⚠️ Shown in the currency your invoices were actually settled in, which is not necessarily your reporting currency. Value delivered is in your reporting currency, so when the two differ the hero shows two different currencies side by side. That is deliberate: restating a settled amount under another currency's symbol would be the same silent relabelling that read-time conversion was rejected for, and showing both makes the divergence visible instead of hiding it.",
    "formula": "Subscription charges plus activation charges across settled invoices, all time.",
    "seeAlso": [
      "invoice",
      "subscription",
      "activation-total-cost",
      "return-on-vera"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "vera-support-access",
    "term": "Vera support access",
    "aliases": [
      "vera support",
      "vera support grant",
      "vera support column"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What Vera's support staff may see and do while working inside your account. You set it in the Vera support column; new companies start with Ask Vera only.",
    "long": "It is the **Vera support** column in Settings → Roles & permissions. Nobody is assigned to it: it applies only to Vera staff while they are inside your account to help you, and it replaces their own access rather than adding to it, so they see exactly what you switched on and nothing more. New companies start with **View AI** switched on, so support can use Ask Vera while helping you; everything else starts off. Ask Vera still only reads data the rest of the column allows. Switch any permission on or off at any time, View AI included. An empty column means Vera support cannot see anything in your account.",
    "seeAlso": [
      "permission",
      "role"
    ]
  },
  {
    "id": "verify",
    "term": "Verify",
    "aliases": [
      "verified",
      "verification",
      "authenticate",
      "authenticity"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Checking that an item is genuine by tapping its tag with a phone. What is checked is the tag answering for itself, not a code or image that could be copied.",
    "long": "The distinction that matters is between reading something and challenging something. A printed code, a QR square or a photograph can be duplicated perfectly, so reading one proves only that a copy exists somewhere. A [tag](term:tag) that can verify is asked a question on each tap that only that individual tag can answer, and a copy of it cannot. This is why opening a [certificate](term:certificate) by following a link is recorded differently from opening it by tapping: both show you the certificate, but only one of them proves the physical item was present.",
    "seeAlso": [
      "scan",
      "tag",
      "certificate",
      "scan-status-verified"
    ]
  },
  {
    "id": "white-label",
    "term": "White label",
    "aliases": [
      "powered by vera",
      "hide powered by"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Removing Vera's name from what your customers see: the badge on a public certificate, the attribution in your emails, and the sender line on your SMS.",
    "long": "White label is only about the ATTRIBUTION. [Putting your own logo, colours and typeface](term:studio) on a [certificate](term:certificate) is not part of it and is not gated: every plan can do that, because a certificate carrying Vera's styling would misrepresent whose item it is. What white label removes is the line saying the page is powered by Vera — on certificate pages, in email footers, and (VRG-487) at the front of every SMS, which otherwise leads with \"{your name} (via vera.inc)\" instead of just your name.",
    "seeAlso": [
      "plan",
      "studio",
      "certificate-view"
    ]
  },
  {
    "id": "email-requirement",
    "term": "Email requirement",
    "aliases": [
      "require email to view",
      "email gate",
      "collect email"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether a visitor must give their email address before a certificate's details open. Set for the whole company, or overridden for one product or one certificate.",
    "long": "A gate, not a prompt: with it on, a visitor who does not enter an email does not see the [certificate](term:certificate)'s details. What they always see is the product preview, so a [scan](term:scan) never dead-ends on a blank page — the item is identified, and the detail behind it is what the email buys. Addresses collected this way are stored against the view, which makes the setting a privacy decision as much as a lead-capture one, so it belongs in whatever privacy notice you publish. The company default is set under Settings, and a single [product](term:product) or a single certificate can override it — useful when a limited run warrants the friction and the rest of the catalogue does not. The most specific setting wins: certificate, then product, then the company default. It resolves independently of the [location requirement](term:location-requirement); either, both or neither can apply to the same certificate.",
    "seeAlso": [
      "location-requirement",
      "certificate",
      "scan",
      "activation-trigger"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "location-requirement",
    "term": "Location requirement",
    "aliases": [
      "require a location to view",
      "location gate",
      "collect location"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether a visitor must share their location before a certificate's details open. Set for the whole company, or overridden for one product or one certificate.",
    "long": "This is a gate and not a request, which is the part worth reading twice. A visitor who declines their browser's location prompt — or who is stood indoors with no usable signal, an ordinary way to tap a tag — does not see the [certificate](term:certificate)'s details. The product preview stays visible either way, so the item is still identified. Turning it on trades reach for data: every [scan](term:scan) that yields a location is one you can place on a map, and every visitor whose device cannot produce one is a reader you turned away. Positions are stored against the scan, so your privacy notice needs to cover them. The company default is set under Settings, and a single [product](term:product) or a single certificate can override it. The most specific setting wins: certificate, then product, then the company default. It resolves independently of the [email requirement](term:email-requirement).",
    "seeAlso": [
      "email-requirement",
      "scan",
      "certificate",
      "scan-location-accuracy"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "item-manufacture-date",
    "term": "Item manufacture date",
    "aliases": [
      "item manufactured",
      "item made date"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When one individual item was made, recorded on its own certificate. Distinct from the product's manufacture date, which applies to the whole model.",
    "long": "A [product](term:product) carries one manufacture date for the design; a [certificate](term:certificate) can carry its own for the single unit it stands for. The two sit side by side on a certificate and answer different questions — when this line was introduced, and when this particular piece came off the bench — so a run made two years after the model launched reads correctly instead of inheriting the model's date. Nothing writes it automatically: it is entered by hand on the certificate, and reads Not set until someone does, which is why leaving it empty is safer than approximating it.",
    "seeAlso": [
      "certificate",
      "product",
      "product-manufacture-date",
      "product-lifespan"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "product-manufacture-date",
    "term": "Product manufacture date",
    "aliases": [
      "product manufactured",
      "model manufacture date"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When the model was made, recorded on the product and shared by every certificate for it. An item with no manufacture date of its own falls back to this in its provenance.",
    "long": "Set on the [product](term:product) and not on the certificate: the row on a certificate is a read-only view of the model's date, so changing it changes what every certificate for that product shows. That is the point of it. A hundred units of one design share one date, and recording it once is the only way that stays true as units are added.\n\nThe relationship with the [item's own date](term:item-manufacture-date) runs one way. Where an item has no date of its own, its [provenance](term:provenance) origin row shows the product's, so a brand that tracks dates only at the model level still gets a dated origin. Where the item does have one, the item's wins and the product's goes on describing the model. The certificate page resolves nothing between them: it shows both rows and labels each with what it belongs to.\n\nIt is also the preferred starting point for [product lifespan](term:product-lifespan), which falls back to certificate creation dates for products that have none. Filling it in is what makes that figure measure an item's age rather than its time on the system.",
    "seeAlso": [
      "item-manufacture-date",
      "product",
      "provenance",
      "product-lifespan"
    ],
    "docs": [
      "products/products",
      "certificates/certificates"
    ]
  },
  {
    "id": "certificate-creation-date",
    "term": "Certificate creation date",
    "aliases": [
      "creation date",
      "certificate created"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When this certificate record was created in Vera, not when the item was made or sold. Certificates are usually minted in bulk, before the item they stand for is finished.",
    "long": "Vera stamps this when the record is written and nothing changes it afterwards. It is the only date on a certificate that describes the record rather than the object, which is why it is the wrong number to reach for when someone asks how old an item is.\n\nWhat it is most often read as is [activation](term:activation), a different moment and the one Vera bills for. A certificate can sit created and inactive for months, so for a brand that activates on sale, the gap between the two dates is roughly how long the item sat in stock.\n\nIt does stand in for age in one place. [Product lifespan](term:product-lifespan) measures from a product's manufacture date where there is one and from the earliest certificate creation date where there is not, so a catalogue imported long after it was made reads younger than it is. Filling in manufacture dates is what moves those products onto the truer basis.",
    "seeAlso": [
      "certificate",
      "activation",
      "item-manufacture-date",
      "product-lifespan"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "refer-a-brand",
    "term": "Refer & earn",
    "kind": "term",
    "audience": "brand",
    "feature": "referrals",
    "aliases": [
      "Refer a brand",
      "refer and earn",
      "referral",
      "referrals",
      "referral programme",
      "referral link",
      "refer and earn",
      "refer & earn"
    ],
    "short": "Introduce another brand to Vera. When they join and pay, your company receives activations, and they get a discount on their base plan.",
    "long": "Every member of your company has their own referral link, and can invite a brand by email from the Refer & earn row in the sidebar. Nothing is credited when someone signs up: [referral credits](term:referral-credits) arrive only when the brand you referred pays Vera. The brand you refer gets 20% off their base plan for their first three months, or 5% off their first annual invoice. A brand that is already a Vera customer, or is already on the waitlist, does not count.",
    "seeAlso": [
      "referral-credits",
      "referral-status-paying"
    ],
    "docs": [
      "personal-settings/referrals"
    ]
  },
  {
    "id": "referral-credits",
    "term": "Referral credits",
    "kind": "formula",
    "audience": "brand",
    "feature": "referrals",
    "aliases": [
      "referral activations",
      "credits from referrals"
    ],
    "short": "Activations your company receives when a brand you referred pays Vera: worth 20% of what they pay in their first 12 months, at least 10 and up to 1,000 per brand.",
    "long": "They are credited each time the referred brand pays an invoice, starting with the first one that takes money, and stop after 12 months or at 1,000 per referral, whichever comes first. The first payment always brings at least 10. They join your company's activation balance and [roll over](term:rollover) like every other credit; they do not expire. If a payment is refunded, the credits it brought in are taken back, but never below a balance of zero. They are activations, not money, and are never paid out.",
    "seeAlso": [
      "refer-a-brand",
      "rollover",
      "activation"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "formula": "Per paid invoice in the first 12 months: 20% of what the referred brand paid (after discounts and tax), at one activation per $125, with any remainder carried to the next invoice. The first paid invoice brings at least 10. Stops at 1,000."
  },
  {
    "id": "referral-status-sent",
    "term": "Sent",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "A referral that has been sent or shared but has not turned into a signup yet.",
    "long": "An invitation shows as Sent from the moment it is handled. That includes an address that will never become a referral, for example a brand that is already on Vera: Vera does not say who its customers are, so those read as Sent too.",
    "seeAlso": [
      "referral-status-signed-up",
      "refer-a-brand"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-signed-up",
    "term": "Signed up",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred has asked to join Vera and is waiting to be approved.",
    "long": "Referred brands are not let in automatically. They wait with everyone else and are reviewed by Vera, though referred brands are reviewed first.",
    "seeAlso": [
      "referral-status-approved"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-approved",
    "term": "Approved",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "Vera has approved the brand you referred, and they can now create their account.",
    "long": "The next step is theirs: once they set up their company on Vera the referral shows as [Joined](term:referral-status-joined).",
    "seeAlso": [
      "referral-status-joined"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-joined",
    "term": "Joined",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred has set up its company on Vera but has not paid yet.",
    "long": "Nothing is credited until they pay. When their first invoice is paid the referral becomes [Paying](term:referral-status-paying).",
    "seeAlso": [
      "referral-status-paying",
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-paying",
    "term": "Paying",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred is paying Vera, and each payment adds referral credits to your company.",
    "long": "Credits keep arriving with each paid invoice for 12 months from their first payment, or until the referral reaches 1,000.",
    "seeAlso": [
      "referral-credits",
      "referral-status-limit-reached",
      "referral-status-ended"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-limit-reached",
    "term": "Limit reached",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral has brought in the most it can: 1,000 activations.",
    "long": "The brand stays on Vera; your company simply receives no more credits from this referral.",
    "seeAlso": [
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-ended",
    "term": "Ended",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral's 12 months of credits have finished.",
    "long": "The 12 months start with the referred brand's first payment. The credits it brought in stay in your company's balance.",
    "seeAlso": [
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-not-eligible",
    "term": "Not eligible",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral does not count, because the address belongs to your own company.",
    "long": "A company cannot refer itself, so an invitation to someone at your own company, or on your company's email domain, is recorded but never earns credits.",
    "seeAlso": [
      "refer-a-brand"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "contact-card",
    "term": "Contact card",
    "kind": "term",
    "audience": "brand",
    "aliases": [
      "business card details",
      "vcard",
      "save contact"
    ],
    "short": "A person's details (name, title, company, phone, email, website, photo and social links) that a product carries and a viewer with access can save to their phone.",
    "long": "Set on the product page, in the Contact card section, and shared by every [certificate](term:certificate) of that [product](term:product). It is not part of the public certificate page: a viewer only receives it once they have access. Where the product asks for an email first, that is after they share theirs; where it does not, anyone who opens the certificate; and the [owner](term:owner) always. The certificate page then shows a Contact section with a Save contact button that adds the person, photo and social links included, to the phone's contacts. Built for business cards, where the tag is the card and the contact card is the person on it. Leave every field blank for no contact card.",
    "seeAlso": [
      "certificate",
      "product",
      "owner",
      "email-requirement"
    ]
  }
] as const
